import React, { createContext, useState, useContext, ReactNode } from 'react';

// Define types for our context
interface FileData {
  file: File | null;
  data: any[] | null;
  processedData: any[] | null;
  classifications: any[] | null;
  selectedColumns: string[];
  s3Upload: {
    isUploaded: boolean;
    location: string | null;
    key: string | null;
    error: string | null;
  };
  apiNotification: {
    isNotified: boolean;
    response: any | null;
    error: string | null;
  };
  processingStatus: {
    isProcessing: boolean;
    progress: number;
    currentStep: string;
  };
}

interface FileDataContextType {
  fileData: FileData;
  setFile: (file: File) => void;
  setData: (data: any[]) => void;
  setProcessedData: (data: any[]) => void;
  setClassifications: (classifications: any[]) => void;
  setSelectedColumns: (columns: string[]) => void;
  setS3UploadSuccess: (location: string, key: string) => void;
  setS3UploadError: (error: string) => void;
  setApiNotificationSuccess: (response: any) => void;
  setApiNotificationError: (error: string) => void;
  startProcessing: () => void;
  updateProcessingProgress: (progress: number, step: string) => void;
  completeProcessing: () => void;
  resetFileData: () => void;
}

const initialFileData: FileData = {
  file: null,
  data: null,
  processedData: null,
  classifications: null,
  selectedColumns: [],
  s3Upload: {
    isUploaded: false,
    location: null,
    key: null,
    error: null
  },
  apiNotification: {
    isNotified: false,
    response: null,
    error: null
  },
  processingStatus: {
    isProcessing: false,
    progress: 0,
    currentStep: '',
  },
};

const FileDataContext = createContext<FileDataContextType | undefined>(undefined);

export const useFileData = () => {
  const context = useContext(FileDataContext);
  if (!context) {
    throw new Error('useFileData must be used within a FileDataProvider');
  }
  return context;
};

interface FileDataProviderProps {
  children: ReactNode;
}

export const FileDataProvider: React.FC<FileDataProviderProps> = ({ children }) => {
  const [fileData, setFileData] = useState<FileData>(initialFileData);

  const setFile = (file: File) => {
    setFileData((prev) => ({
      ...prev,
      file,
    }));
  };

  const setData = (data: any[]) => {
    setFileData((prev) => ({
      ...prev,
      data,
    }));
  };

  const setProcessedData = (processedData: any[]) => {
    setFileData((prev) => ({
      ...prev,
      processedData,
    }));
  };

  const setClassifications = (classifications: any[]) => {
    setFileData((prev) => ({
      ...prev,
      classifications,
    }));
  };

  const setSelectedColumns = (columns: string[]) => {
    setFileData((prev) => ({
      ...prev,
      selectedColumns: columns,
    }));
  };

  const setS3UploadSuccess = (location: string, key: string) => {
    setFileData((prev) => ({
      ...prev,
      s3Upload: {
        isUploaded: true,
        location,
        key,
        error: null
      }
    }));
  };

  const setS3UploadError = (error: string) => {
    setFileData((prev) => ({
      ...prev,
      s3Upload: {
        ...prev.s3Upload,
        isUploaded: false,
        error
      }
    }));
  };

  const setApiNotificationSuccess = (response: any) => {
    setFileData((prev) => ({
      ...prev,
      apiNotification: {
        isNotified: true,
        response,
        error: null
      }
    }));
  };

  const setApiNotificationError = (error: string) => {
    setFileData((prev) => ({
      ...prev,
      apiNotification: {
        ...prev.apiNotification,
        isNotified: false,
        error
      }
    }));
  };

  const startProcessing = () => {
    setFileData((prev) => ({
      ...prev,
      processingStatus: {
        isProcessing: true,
        progress: 0,
        currentStep: 'Initializing...',
      },
    }));
  };

  const updateProcessingProgress = (progress: number, currentStep: string) => {
    setFileData((prev) => ({
      ...prev,
      processingStatus: {
        ...prev.processingStatus,
        progress,
        currentStep,
      },
    }));
  };

  const completeProcessing = () => {
    setFileData((prev) => ({
      ...prev,
      processingStatus: {
        isProcessing: false,
        progress: 100,
        currentStep: 'Complete',
      },
    }));
  };

  const resetFileData = () => {
    setFileData(initialFileData);
  };

  const value = {
    fileData,
    setFile,
    setData,
    setProcessedData,
    setClassifications,
    setSelectedColumns,
    setS3UploadSuccess,
    setS3UploadError,
    setApiNotificationSuccess,
    setApiNotificationError,
    startProcessing,
    updateProcessingProgress,
    completeProcessing,
    resetFileData,
  };

  return <FileDataContext.Provider value={value}>{children}</FileDataContext.Provider>;
}; 